/**
 * BlockingInvoicesDialog
 *
 * Shared dialog surfaced whenever a contract change (back-dated schedule change,
 * in-place correction, version-period adjust, or version cancel) collides with
 * a non-cancelled invoice. Lists every colliding invoice as a deep link to the
 * invoices page and offers a single "Cancel & proceed" that cancels each via the
 * canonical cash-in release path (`useCancelAndProceed`) and re-runs the change.
 *
 * R17: when any colliding invoice is sent/paid (the parent has received/paid
 * it), cancelling releases their money to credit — so an explicit confirmation
 * checkbox is required before "Cancel & proceed" is enabled. Draft-only
 * collisions cancel frictionlessly.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react";
import { msg } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";
import { AlertTriangle, ExternalLink, Wallet } from "lucide-react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { Checkbox } from "@/components/ui/checkbox";
import type { BlockingInvoiceRef } from "@/utils/contractErrorMessages";

interface BlockingInvoicesDialogProps {
  open: boolean;
  invoices: BlockingInvoiceRef[];
  onCancelAndProceed: () => void | Promise<void>;
  onClose: () => void;
  isProcessing?: boolean;
  /**
   * Optional title/description overrides. Defaults to the #722 schedule-change
   * copy; the contract-termination flow passes its own copy (the server-side
   * saga cancels AND re-issues automatically, so the admin does not regenerate
   * invoices manually).
   */
  title?: string;
  description?: string;
}

// Backend invoice statuses (lowercase) that mean the parent has already
// received/paid the invoice — cancelling these releases their money to credit.
const SENSITIVE_STATUSES = new Set(["sent", "paid", "partial", "overdue"]);

// Plain-language labels for the raw lowercase invoice-status enum so a clerk
// never sees "sent"/"paid"/"partial" verbatim on the badge. Covers the full
// invoice-status contract (see `InvoiceStatus` in types/billing); any value not
// mapped here falls back to the generic plain-language label below.
const STATUS_LABEL: Record<string, MessageDescriptor> = {
  draft: msg({ id: "contracts.blockingInvoices.status.draft", message: "Draft" }),
  finalized: msg({ id: "contracts.blockingInvoices.status.finalized", message: "Finalised" }),
  sent: msg({ id: "contracts.blockingInvoices.status.sent", message: "Sent" }),
  paid: msg({ id: "contracts.blockingInvoices.status.paid", message: "Paid" }),
  partial: msg({ id: "contracts.blockingInvoices.status.partial", message: "Part-paid" }),
  overdue: msg({ id: "contracts.blockingInvoices.status.overdue", message: "Overdue" }),
  cancelled: msg({ id: "contracts.blockingInvoices.status.cancelled", message: "Cancelled" }),
  credited: msg({ id: "contracts.blockingInvoices.status.credited", message: "Credited" }),
};

// Generic fallback so an unexpected backend status still reads in plain language
// instead of leaking the raw enum value onto the badge.
const STATUS_LABEL_FALLBACK: MessageDescriptor = msg({
  id: "contracts.blockingInvoices.status.unknown",
  message: "Issued",
});

export const BlockingInvoicesDialog =
  React.memo<BlockingInvoicesDialogProps>(
    ({ open, invoices, onCancelAndProceed, onClose, isProcessing = false, title, description }) => {
      const { i18n } = useLingui();
      const [confirmed, setConfirmed] = useState(false);

      const hasSensitive = invoices.some((inv) =>
        SENSITIVE_STATUSES.has(inv.status.toLowerCase()),
      );

      // Reset the confirmation whenever the dialog opens, so a prior
      // confirmation can't carry over. Gated on `open` only — parents pass a
      // fresh `invoices` array each render, so depending on it would reset the
      // checkbox needlessly while the dialog stays open.
      useEffect(() => {
        if (open) {
          setConfirmed(false);
        }
      }, [open]);

      const canProceed = (!hasSensitive || confirmed) && !isProcessing;

      return (
        <Dialog
          open={open}
          onOpenChange={(next) => !next && !isProcessing && onClose()}
        >
          <DialogContent
            aria-describedby="blocking-invoices-description"
            size="md"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle
                  className="h-5 w-5 text-warning-on-surface"
                  aria-hidden="true"
                />
                {title ??
                  i18n._({
                    id: "contracts.blockingInvoices.title",
                    message: "Issued invoices block this change",
                  })}
              </DialogTitle>
              <DialogDescription id="blocking-invoices-description">
                {description ??
                  i18n._({
                    id: "contracts.blockingInvoices.descriptionV2",
                    message:
                      "This change affects month(s) already covered by the invoice(s) below. To proceed, those invoices are cancelled first — then you can re-bill the affected months with the corrected amounts.",
                  })}
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <ul className="my-2 flex flex-col gap-2">
                {invoices.map((invoice) => {
                  const sensitive = SENSITIVE_STATUSES.has(
                    invoice.status.toLowerCase(),
                  );
                  return (
                    <li
                      key={invoice.id}
                      className="flex items-center justify-between gap-2 rounded-md border p-2"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{invoice.period}</span>
                        <StatusBadge
                          tone={sensitive ? "danger" : "neutral"}
                          className="w-fit"
                          label={i18n._(
                            STATUS_LABEL[invoice.status.toLowerCase()] ??
                              STATUS_LABEL_FALLBACK,
                          )}
                        />
                      </div>
                      <Link
                        to={`/billing/invoices?invoice_id=${encodeURIComponent(invoice.id)}`}
                        className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
                        aria-label={i18n._({
                          id: "contracts.blockingInvoices.viewInvoice",
                          message: "View invoice",
                        })}
                      >
                        <Trans id="contracts.blockingInvoices.view">View</Trans>
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {hasSensitive && (
                <div className="flex items-start gap-2 rounded-md border bg-info-muted p-3 text-sm">
                  <Wallet
                    className="mt-0.5 h-4 w-4 shrink-0 text-info-on-surface"
                    aria-hidden="true"
                  />
                  <span>
                    {i18n._({
                      id: "contracts.blockingInvoices.moneyDestination",
                      message:
                        "Nothing is lost: any money already paid returns as credit on the family's account — and on the city/office's account for the subsidised share. You can re-bill the months afterwards and the credit re-applies.",
                    })}
                  </span>
                </div>
              )}

              {hasSensitive && (
                <label className="flex items-start gap-2 rounded-md bg-warning-muted p-3 text-sm text-warning-on-surface">
                  <Checkbox
                    checked={confirmed}
                    onCheckedChange={(value) => setConfirmed(value === true)}
                    disabled={isProcessing}
                    aria-label={i18n._({
                      id: "contracts.blockingInvoices.confirmSensitiveLabel",
                      message:
                        "Confirm cancelling already-sent or paid invoices",
                    })}
                  />
                  <span>
                    {i18n._({
                      id: "contracts.blockingInvoices.confirmSensitive",
                      message:
                        "I understand the parent already received or paid one or more of these invoices; cancelling releases their money to credit.",
                    })}
                  </span>
                </label>
              )}
            </DialogBody>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isProcessing}
              >
                {i18n._({
                  id: "contracts.blockingInvoices.keepInvoices",
                  message: "Keep invoices",
                })}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => void onCancelAndProceed()}
                disabled={!canProceed}
              >
                {isProcessing
                  ? i18n._({
                      id: "contracts.blockingInvoices.processing",
                      message: "Cancelling…",
                    })
                  : i18n._({
                      id: "contracts.blockingInvoices.cancelAndProceed",
                      message: "Cancel & proceed",
                    })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    },
  );

BlockingInvoicesDialog.displayName = "BlockingInvoicesDialog";
